import { useEffect, useState } from "react";
import { X, ArrowsClockwise, Globe, ShieldCheck, Trash, Plus, Warning, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const RECORD_TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "NS", "SRV", "CAA"];

function TotpPrompt({ label, value, onChange }) {
    return (
        <div>
            <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                {label} — enter your 6-digit authenticator code
            </span>
            <input
                data-testid="totp-code-input"
                inputMode="numeric"
                maxLength={6}
                value={value}
                onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="glass-input mono text-center text-lg tracking-widest"
                placeholder="123456"
            />
        </div>
    );
}

export function DomainDetailModal({ domain, onClose, onChanged }) {
    const [tab, setTab] = useState("overview"); // overview | renew | nameservers | dns
    const [live, setLive] = useState(null);
    const [dns, setDns] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [totp, setTotp] = useState("");
    const [years, setYears] = useState(1);
    const [nsInput, setNsInput] = useState("");
    const [records, setRecords] = useState([]);

    const loadDetails = async () => {
        setLoading(true);
        try {
            const [detailsRes, dnsRes] = await Promise.all([
                api.get(`/domain-actions/${domain.id}/details`),
                api.get(`/domain-actions/${domain.id}/dns`).catch(() => ({ data: { ok: false, records: [] } })),
            ]);
            setLive(detailsRes.data.data || detailsRes.data);
            setDns(dnsRes.data);
            const currentNs = (detailsRes.data.data?.nameServers || detailsRes.data.nameServers || domain.nameservers || []).join("\n");
            setNsInput(currentNs);
            setRecords(dnsRes.data.records || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (domain) loadDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain?.id]);

    if (!domain) return null;

    const doRenew = async () => {
        if (!totp || totp.length !== 6) return toast.error("Enter your 6-digit TOTP code");
        setBusy(true);
        try {
            const { data } = await api.post(`/domain-actions/${domain.id}/renew`, { years, totp_code: totp });
            if (data.ok) {
                toast.success(`Renewed ${domain.domain_name} for ${years}y`);
                onChanged?.();
                setTotp("");
            } else {
                toast.error(data.error?.message || JSON.stringify(data.error) || "Renewal failed");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const doSaveNs = async () => {
        if (!totp || totp.length !== 6) return toast.error("Enter your 6-digit TOTP code");
        const ns = nsInput.split(/[\s,]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
        if (ns.length < 2) return toast.error("Provide at least 2 nameservers");
        setBusy(true);
        try {
            const { data } = await api.put(`/domain-actions/${domain.id}/nameservers`, { nameservers: ns, totp_code: totp });
            if (data.ok) {
                toast.success("Nameservers updated");
                onChanged?.();
                setTotp("");
                loadDetails();
            } else {
                toast.error(data.error?.message || JSON.stringify(data.error) || "Update failed");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const addRecord = () => setRecords([...records, { type: "A", name: "@", data: "", ttl: 1800, priority: null }]);
    const updateRecord = (idx, patch) => setRecords(records.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const deleteRecord = (idx) => setRecords(records.filter((_, i) => i !== idx));

    const doSaveDns = async () => {
        if (!totp || totp.length !== 6) return toast.error("Enter your 6-digit TOTP code");
        setBusy(true);
        try {
            const payload = { records: records.filter((r) => r.type && r.data), totp_code: totp };
            const { data } = await api.put(`/domain-actions/${domain.id}/dns`, payload);
            if (data.ok) {
                toast.success("DNS records saved");
                onChanged?.();
                setTotp("");
                loadDetails();
            } else {
                toast.error(JSON.stringify(data.errors || data.error) || "DNS update failed");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,45,0.4)", backdropFilter: "blur(6px)" }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="glass-strong w-full max-w-4xl p-8 max-h-[92vh] overflow-auto"
                data-testid="domain-detail-modal"
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="text-2xl font-extrabold mono" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                            {domain.domain_name}
                        </h2>
                        <div className="flex items-center gap-2 mt-1 text-xs">
                            <span className={`pill ${domain.registrar === "godaddy" ? "pill-warn" : "pill-brand"}`}>
                                {domain.registrar}
                            </span>
                            <span className="mono text-[color:var(--dm-text-2)]">{domain.registrar_account_name}</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="btn-ghost !p-2" data-testid="close-modal">
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <div className="flex gap-2 mb-5 border-b border-[rgba(15,41,89,0.08)] pb-2">
                    {["overview", "renew", "nameservers", "dns"].map((t) => (
                        <button
                            key={t}
                            onClick={() => setTab(t)}
                            data-testid={`tab-${t}`}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                tab === t
                                    ? "bg-[rgba(0,122,255,0.1)] text-[color:var(--dm-brand)]"
                                    : "text-[color:var(--dm-text-2)] hover:bg-[rgba(0,122,255,0.05)]"
                            }`}
                        >
                            {t === "overview" && "Overview"}
                            {t === "renew" && "Renew"}
                            {t === "nameservers" && "Nameservers"}
                            {t === "dns" && "DNS Records"}
                        </button>
                    ))}
                </div>

                {loading && <div className="p-6 text-sm text-[color:var(--dm-text-2)]">Loading live data…</div>}

                {!loading && tab === "overview" && (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        {[
                            ["Status", live?.status || domain.status],
                            ["Expires", live?.expires ? new Date(live.expires).toLocaleString() : "—"],
                            ["Locked", String(live?.locked ?? domain.locked ?? "—")],
                            ["Privacy", String(live?.privacy ?? domain.privacy ?? "—")],
                            ["Auto-renew", String(live?.renewAuto ?? domain.auto_renew ?? "—")],
                            ["Created", live?.createdAt ? new Date(live.createdAt).toLocaleString() : "—"],
                        ].map(([label, val]) => (
                            <div key={label} className="glass !rounded-xl p-3">
                                <div className="text-xs text-[color:var(--dm-text-2)] uppercase tracking-wider mb-0.5">{label}</div>
                                <div className="mono">{val}</div>
                            </div>
                        ))}
                        <div className="glass !rounded-xl p-3 col-span-2">
                            <div className="text-xs text-[color:var(--dm-text-2)] uppercase tracking-wider mb-1">Nameservers</div>
                            <div className="mono text-sm space-y-0.5">
                                {(live?.nameServers || domain.nameservers || []).map((ns, i) => (
                                    <div key={i}>{ns}</div>
                                )) || "—"}
                            </div>
                        </div>
                    </div>
                )}

                {!loading && tab === "renew" && (
                    <div className="space-y-4 max-w-md">
                        <label>
                            <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Years</span>
                            <select value={years} onChange={(e) => setYears(Number(e.target.value))} className="glass-input" data-testid="renew-years">
                                {[1, 2, 3, 5, 10].map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </label>
                        <TotpPrompt label="Confirm renewal" value={totp} onChange={setTotp} />
                        <button onClick={doRenew} disabled={busy || totp.length !== 6} className="btn-glossy" data-testid="renew-submit">
                            <ArrowsClockwise size={14} weight="bold" /> {busy ? "Renewing…" : `Renew for ${years}y`}
                        </button>
                        <div className="text-xs text-[color:var(--dm-text-2)]">
                            ⚠️ This charges the payment method on file for the registrar account.
                        </div>
                    </div>
                )}

                {!loading && tab === "nameservers" && (
                    <div className="space-y-4">
                        <label className="block">
                            <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                                Nameservers (one per line or comma-separated, minimum 2)
                            </span>
                            <textarea
                                value={nsInput}
                                onChange={(e) => setNsInput(e.target.value)}
                                className="glass-input mono !min-h-[140px]"
                                placeholder="ns1.cloudflare.com&#10;ns2.cloudflare.com"
                                data-testid="ns-input"
                            />
                        </label>
                        <TotpPrompt label="Confirm nameserver change" value={totp} onChange={setTotp} />
                        <button onClick={doSaveNs} disabled={busy || totp.length !== 6} className="btn-glossy" data-testid="ns-submit">
                            <Globe size={14} weight="bold" /> {busy ? "Saving…" : "Save nameservers"}
                        </button>
                    </div>
                )}

                {!loading && tab === "dns" && (
                    <div className="space-y-3">
                        {domain.registrar === "namecheap" && (
                            <div className="glass !rounded-xl p-3 bg-[rgba(255,149,0,0.08)] text-xs flex gap-2">
                                <Warning size={16} weight="duotone" className="text-[color:var(--dm-warn)] shrink-0" />
                                <span><b>Namecheap replaces all records at once.</b> Whatever you save here becomes the complete record set — deletes anything not in this list.</span>
                            </div>
                        )}                        <div className="glass !rounded-xl overflow-hidden">
                            <div className="grid grid-cols-[100px_1.2fr_2fr_80px_80px_40px] gap-2 px-3 py-2 text-[0.7rem] uppercase tracking-wider text-[color:var(--dm-text-2)] font-bold bg-[rgba(0,122,255,0.05)]">
                                <div>Type</div>
                                <div>Name</div>
                                <div>Data</div>
                                <div>TTL</div>
                                <div>Pri.</div>
                                <div></div>
                            </div>
                            <div className="max-h-[40vh] overflow-y-auto">
                                {records.map((r, i) => (
                                    <div key={i} className="grid grid-cols-[100px_1.2fr_2fr_80px_80px_40px] gap-2 px-3 py-1.5 items-center border-b border-[rgba(15,41,89,0.04)]" data-testid={`dns-row-${i}`}>
                                        <select value={r.type} onChange={(e) => updateRecord(i, { type: e.target.value })} className="glass-input mono !py-1 !text-xs">
                                            {RECORD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <input value={r.name} onChange={(e) => updateRecord(i, { name: e.target.value })} className="glass-input mono !py-1 !text-xs" />
                                        <input value={r.data} onChange={(e) => updateRecord(i, { data: e.target.value })} className="glass-input mono !py-1 !text-xs" />
                                        <input type="number" value={r.ttl} onChange={(e) => updateRecord(i, { ttl: Number(e.target.value) })} className="glass-input mono !py-1 !text-xs" />
                                        <input type="number" value={r.priority ?? ""} onChange={(e) => updateRecord(i, { priority: e.target.value ? Number(e.target.value) : null })} className="glass-input mono !py-1 !text-xs" placeholder={r.type === "MX" ? "10" : ""} />
                                        <button onClick={() => deleteRecord(i)} className="btn-ghost btn-danger !py-1 !px-2 text-xs" data-testid={`dns-delete-${i}`}>
                                            <Trash size={12} weight="duotone" />
                                        </button>
                                    </div>
                                ))}
                                {records.length === 0 && (
                                    <div className="p-6 text-sm text-[color:var(--dm-text-2)] text-center">
                                        No DNS records — click &ldquo;Add record&rdquo; below.
                                    </div>
                                )}
                            </div>
                        </div>
                        <button onClick={addRecord} className="btn-ghost text-xs" data-testid="dns-add">
                            <Plus size={12} weight="bold" /> Add record
                        </button>
                        <TotpPrompt label={`Confirm DNS save (${records.length} record${records.length === 1 ? "" : "s"})`} value={totp} onChange={setTotp} />
                        <button onClick={doSaveDns} disabled={busy || totp.length !== 6} className="btn-glossy" data-testid="dns-submit">
                            <CheckCircle size={14} weight="bold" /> {busy ? "Saving…" : "Save DNS records"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
